import React, { useContext, useMemo } from 'react';
import { useControlledState } from '@rc-component/util';
import { clsx } from 'clsx';
import ContextIsolator from '../_util/ContextIsolator';
import { useMergeSemantic } from '../_util/hooks';
import genPurePanel from '../_util/PurePanel';
import { getStatusClassNames } from '../_util/statusUtils';
import { devUseWarning } from '../_util/warning';
import { useComponentConfig } from '../config-provider/context';
import DisabledContext from '../config-provider/DisabledContext';
import useCSSVarCls from '../config-provider/hooks/useCSSVarCls';
import useSize from '../config-provider/hooks/useSize';
import { FormItemInputContext } from '../form/context';
import Popover from '../popover';
import { useCompactItemContext } from '../space/Compact';
import { AggregationColor } from './color';
import ColorPickerPanel from './ColorPickerPanel';
import ColorTrigger from './components/ColorTrigger';
import useModeColor from './hooks/useModeColor';
import useStyle from './style';
import { genAlphaColor, generateColor, getColorAlpha } from './util';
const ColorPicker = (props) => {
    const { mode, value, defaultValue, format, defaultFormat, allowClear = false, presets, children, trigger = 'click', open, disabled, placement = 'bottomLeft', arrow = true, panelRender, showText, style, className, size: customizeSize, rootClassName, prefixCls: customizePrefixCls, styles, classNames, disabledAlpha = false, onFormatChange, onChange, onClear, onOpenChange, onChangeComplete, getPopupContainer, autoAdjustOverflow = true, destroyTooltipOnHide, destroyOnHidden, disabledFormat, ...rest } = props;
    const { getPrefixCls, direction, className: contextClassName, style: contextStyle, classNames: contextClassNames, styles: contextStyles, } = useComponentConfig('colorPicker');
    const contextDisabled = useContext(DisabledContext);
    const mergedDisabled = disabled ?? contextDisabled;
    const prefixCls = getPrefixCls('color-picker', customizePrefixCls);
    // ================== Size ==================
    const { compactSize, compactItemClassnames } = useCompactItemContext(prefixCls, direction);
    const mergedSize = useSize((ctx) => customizeSize ?? compactSize ?? ctx);
    // =========== Merged Props for Semantic ===========
    const mergedProps = {
        ...props,
        trigger,
        allowClear,
        autoAdjustOverflow,
        disabledAlpha,
        arrow,
        placement,
        disabled: mergedDisabled,
        size: mergedSize,
    };
    const [mergedClassNames, mergedStyles] = useMergeSemantic([contextClassNames, classNames], [contextStyles, styles], {
        props: mergedProps,
    }, {
        popup: {
            _default: 'root',
        },
    });
    const [internalPopupOpen, setPopupOpen] = useControlledState(false, open);
    const popupOpen = !mergedDisabled && internalPopupOpen;
    const [formatValue, setFormatValue] = useControlledState(defaultFormat, format);
    const triggerFormatChange = (newFormat) => {
        setFormatValue(newFormat);
        if (formatValue !== newFormat) {
            onFormatChange?.(newFormat);
        }
    };
    const triggerOpenChange = (visible) => {
        if (!visible || !mergedDisabled) {
            setPopupOpen(visible);
            onOpenChange?.(visible);
        }
    };
    // ================== Value & Mode =================
    const [mergedColor, setColor, modeState, setModeState, modeOptions] = useModeColor(defaultValue, value, mode);
    const isAlphaColor = useMemo(() => getColorAlpha(mergedColor) < 100, [mergedColor]);
    // ==================== Change =====================
    // To enhance user experience, we cache the gradient color when switch from gradient to single
    // If user not modify single color, we will use the cached gradient color.
    const [cachedGradientColor, setCachedGradientColor] = React.useState(null);
    const onInternalChangeComplete = (color) => {
        if (onChangeComplete) {
            let changeColor = generateColor(color);
            // ignore alpha color
            if (disabledAlpha && isAlphaColor) {
                changeColor = genAlphaColor(color);
            }
            onChangeComplete(changeColor);
        }
    };
    const onInternalChange = (data, changeFromPickerDrag) => {
        let color = generateColor(data);
        // ignore alpha color
        if (disabledAlpha && isAlphaColor) {
            color = genAlphaColor(color);
        }
        setColor(color);
        setCachedGradientColor(null);
        // Trigger change event
        if (onChange) {
            onChange(color, color.toCssString());
        }
        // Only for drag-and-drop color picking
        if (!changeFromPickerDrag) {
            onInternalChangeComplete(color);
        }
    };
    // =================== Gradient ====================
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [gradientDragging, setGradientDragging] = React.useState(false);
    // Mode change should also trigger color change
    const onInternalModeChange = (newMode) => {
        setModeState(newMode);
        if (newMode === 'single' && mergedColor.isGradient()) {
            setActiveIndex(0);
            onInternalChange(new AggregationColor(mergedColor.getColors()[0].color));
            // Should after `onInternalChange` since it will clear the cached color
            setCachedGradientColor(mergedColor);
        }
        else if (newMode === 'gradient' && !mergedColor.isGradient()) {
            const baseColor = isAlphaColor ? genAlphaColor(mergedColor) : mergedColor;
            onInternalChange(new AggregationColor(cachedGradientColor || [
                {
                    percent: 0,
                    color: baseColor,
                },
                {
                    percent: 100,
                    color: baseColor,
                },
            ]));
        }
    };
    // ================== Form Status ==================
    const { status: contextStatus } = React.useContext(FormItemInputContext);
    // ===================== Style =====================
    const rootCls = useCSSVarCls(prefixCls);
    const [hashId, cssVarCls] = useStyle(prefixCls, rootCls);
    const rtlCls = { [`${prefixCls}-rtl`]: direction };
    const mergedRootCls = clsx(mergedClassNames.root, rootClassName, cssVarCls, rootCls, rtlCls);
    const mergedCls = clsx(getStatusClassNames(prefixCls, contextStatus), {
        [`${prefixCls}-sm`]: mergedSize === 'small',
        [`${prefixCls}-lg`]: mergedSize === 'large',
    }, compactItemClassnames, contextClassName, mergedRootCls, className, hashId);
    const mergedPopupCls = clsx(prefixCls, mergedRootCls, mergedClassNames.popup?.root);
    // ===================== Warning ======================
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('ColorPicker');
        warning(!(disabledAlpha && isAlphaColor), 'usage', '`disabledAlpha` will make the alpha to be 100% when use alpha color.');
    }
    const popoverProps = {
        open: popupOpen,
        trigger,
        placement,
        arrow,
        rootClassName,
        getPopupContainer,
        autoAdjustOverflow,
        destroyOnHidden: destroyOnHidden ?? !!destroyTooltipOnHide,
    };
    const mergedStyle = { ...mergedStyles.root, ...contextStyle, ...style };
    // ============================ zIndex ============================
    return (React.createElement(Popover, { classNames: { root: mergedPopupCls }, styles: { root: mergedStyles.popup?.root, container: styles?.popupOverlayInner }, onOpenChange: triggerOpenChange, content: React.createElement(ContextIsolator, { form: true },
            React.createElement(ColorPickerPanel, { mode: modeState, onModeChange: onInternalModeChange, modeOptions: modeOptions, prefixCls: prefixCls, value: mergedColor, allowClear: allowClear, disabled: mergedDisabled, disabledAlpha: disabledAlpha, presets: presets, panelRender: panelRender, format: formatValue, onFormatChange: triggerFormatChange, onChange: onInternalChange, onChangeComplete: onInternalChangeComplete, onClear: onClear, activeIndex: activeIndex, onActive: setActiveIndex, gradientDragging: gradientDragging, onGradientDragging: setGradientDragging, disabledFormat: disabledFormat })), ...popoverProps }, children || (React.createElement(ColorTrigger, { activeIndex: popupOpen ? activeIndex : -1, open: popupOpen, className: mergedCls, style: mergedStyle, prefixCls: prefixCls, disabled: mergedDisabled, showText: showText, format: formatValue, ...rest, color: mergedColor }))));
};
if (process.env.NODE_ENV !== 'production') {
    ColorPicker.displayName = 'ColorPicker';
}
const PurePanel = genPurePanel(ColorPicker, undefined, (props) => ({
    ...props,
    placement: 'bottom',
    autoAdjustOverflow: false,
}), 'color-picker', 
/* istanbul ignore next */
(prefixCls) => prefixCls);
ColorPicker._InternalPanelDoNotUseOrYouWillBeFired = PurePanel;
export default ColorPicker;
